import { createContext, useContext, useState, ReactNode } from 'react'
import apiClient from '../api/client'

type ModelCodename =
  | 'strawberry'
  | 'strawberry-pro'
  | 'strawberry-pro2'
  | 'blueberry'
  | 'blueberry-45'
  | 'lemon-lux'
  | 'lemon-max'
  | 'lemon-max2-edit'
  | 'plum-v2'
  | 'plumv2-fast'
  | 'sunburst'
  | 'flare'
type GenerationStatus = 'idle' | 'generating' | 'success' | 'error'
type AspectRatioOption = 'none' | '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '4:5' | '5:4' | '2:3' | '3:2' | '1:2' | '21:9'
type ImageSizeOption = '1K' | '2K' | '4K'
// Plum v2 (gpt-image-2) uses explicit pixel dimensions instead of 1K/2K/4K buckets.
type PlumV2SizeOption =
  | '1024x1024'
  | '1024x1536'
  | '1536x1152'
  | '1536x1024'
  | '1152x2048'
  | '2048x2048'
  | '2048x1152'
  | '3840x2160'
  | '2160x3840'
  | 'auto'
type PlumV2QualityOption = 'low' | 'medium' | 'high' | 'xhigh' | 'max' | 'auto'
type ResolutionOption = '1k' | '2k' | '2k_portrait' | '4k' | '4k_portrait' | 'fashion_4k'
type OutputFormatOption = 'PNG' | 'JPEG' | 'WEBP' | 'AVIF'
type FaceMode = 'single' | 'batch'
type PoseMode = 'single' | 'batch'
type GarmentMode = 'single' | 'batch'

const TRANSPARENT_BACKGROUND_MODELS: ModelCodename[] = ['plumv2-fast', 'sunburst', 'flare']

export const appendStylistaImageOptions = (
  formData: FormData,
  model: ModelCodename,
  size: string,
  quality: string,
  transparentBackground: boolean
) => {
  formData.append('size', size)
  formData.append('quality', quality)
  if (transparentBackground && TRANSPARENT_BACKGROUND_MODELS.includes(model)) {
    formData.append('transparent_background', 'true')
  }
}

const normalizePreviewUrls = (urls: string[]): string[] => (
  urls
    .filter((url): url is string => Boolean(url))
    .map((url) => {
      const trimmed = url.trim()
      if (!trimmed) return trimmed
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
      if (trimmed.startsWith('/static/')) return trimmed
      if (trimmed.startsWith('/')) return `/static/${trimmed.replace(/^\/+/, '')}`
      return `/static/${trimmed}`
    })
)

export interface PromptPreset {
  id: string
  label: string
  prompt: string
  description?: string
}

const DEFAULT_PROMPT_PRESETS: PromptPreset[] = [
  { id: 'empty', label: 'Pusty', prompt: '', description: 'Bez dodatkowego tekstu.' },
  {
    id: 'create-model',
    label: 'Stwórz modelkę',
    prompt: 'Stwórz modelkę.\nDress the model in a white one-piece swimsuit. Cream-colored high heels. Cream-colored background, fashion-style lighting.'
  }
]

export interface BatchJobResult {
  poseName: string | null
  faceName: string | null
  garmentTopName: string | null
  garmentBottomName: string | null
  success: boolean
  message?: string
  previewUrls?: string[]
  modelResponse?: string
  modelName?: ModelCodename
}

export interface BatchRunInfo {
  enabled: boolean
  poseMode: PoseMode
  faceMode: FaceMode
  garmentTopMode: GarmentMode
  garmentBottomMode: GarmentMode
  total: number
  completed: number
  activeIndex: number
  results: BatchJobResult[]
}

interface GenerationResult {
  status: GenerationStatus
  previewUrls?: string[]
  message?: string
  imagesGenerated?: number
  metadata?: any
  batchInfo?: BatchRunInfo
  modelResponse?: string
}

interface StylistaState {
  faceImage: File | null
  faceMode: FaceMode
  faceBatch: File[]
  poseImage: File | null
  garmentTopMode: GarmentMode
  garmentTopBatch: File[]
  garmentBottomMode: GarmentMode
  garmentBottomBatch: File[]
  silhouetteImage: File | null
  backgroundImage: File | null
  lightingImage: File | null
  garmentTopImage: File | null
  garmentBottomImage: File | null
  garmentAdditionalImage: File | null
  garmentExtraImage: File | null
  bagImage: File | null
  glassesImage: File | null
  shoesImage: File | null
  jewelryImage: File | null
  selectedModel: ModelCodename
  selectedModels: ModelCodename[]
  multiModelEnabled: boolean
  aspectRatio: AspectRatioOption
  imageSize: ImageSizeOption
  resolution: ResolutionOption
  // Plum v2 (gpt-image-2) — separate knobs because it uses raw WxH instead of 1K/2K/4K buckets.
  plumSize: PlumV2SizeOption
  plumQuality: PlumV2QualityOption
  transparentBackground: boolean
  outputFormat: OutputFormatOption
  fullPromptOverride: string
  prompt: string
  promptPresetId: string
  promptPresets: PromptPreset[]
  poseMode: PoseMode
  poseBatch: File[]
  generationResult: GenerationResult
}

type SlotName =
  | 'faceImage'
  | 'poseImage'
  | 'silhouetteImage'
  | 'backgroundImage'
  | 'lightingImage'
  | 'garmentTopImage'
  | 'garmentBottomImage'
  | 'garmentAdditionalImage'
  | 'garmentExtraImage'
  | 'bagImage'
  | 'glassesImage'
  | 'shoesImage'
  | 'jewelryImage'

interface StylistaContextType extends StylistaState {
  setFile: (slot: SlotName, file: File | null) => void
  clearSlot: (slot: SlotName) => void
  setFaceMode: (mode: FaceMode) => void
  addFaceFiles: (files: File[]) => void
  removeFaceFile: (index: number) => void
  clearFaceBatch: () => void
  setGarmentTopMode: (mode: GarmentMode) => void
  addGarmentTopFiles: (files: File[]) => void
  removeGarmentTopFile: (index: number) => void
  clearGarmentTopBatch: () => void
  setGarmentBottomMode: (mode: GarmentMode) => void
  addGarmentBottomFiles: (files: File[]) => void
  removeGarmentBottomFile: (index: number) => void
  clearGarmentBottomBatch: () => void
  setSelectedModel: (model: ModelCodename) => void
  setSelectedModels: (models: ModelCodename[]) => void
  setMultiModelEnabled: (enabled: boolean) => void
  setAspectRatio: (ratio: AspectRatioOption) => void
  setImageSize: (size: ImageSizeOption) => void
  setResolution: (r: ResolutionOption) => void
  setPlumSize: (s: PlumV2SizeOption) => void
  setPlumQuality: (q: PlumV2QualityOption) => void
  setTransparentBackground: (enabled: boolean) => void
  setOutputFormat: (f: OutputFormatOption) => void
  setFullPromptOverride: (text: string) => void
  setPrompt: (text: string) => void
  setPromptPresetId: (id: string) => void
  setPromptPresets: (list: PromptPreset[]) => void
  setPoseMode: (mode: PoseMode) => void
  addPoseFiles: (files: File[]) => void
  removePoseFile: (index: number) => void
  clearPoseBatch: () => void
  generateImage: (projectName?: string, styleInjection?: string) => Promise<boolean>
  resetState: () => void
}

const StylistaContext = createContext<StylistaContextType | undefined>(undefined)

export const useStylista = () => {
  const ctx = useContext(StylistaContext)
  if (!ctx) throw new Error('useStylista must be used within StylistaProvider')
  return ctx
}

export const StylistaProvider = ({ children }: { children: ReactNode }) => {
  const [faceImage, setFaceImage] = useState<File | null>(null)
  const [faceModeState, setFaceModeState] = useState<FaceMode>('single')
  const [faceBatch, setFaceBatch] = useState<File[]>([])
  const [poseImage, setPoseImage] = useState<File | null>(null)
  const [poseModeState, setPoseModeState] = useState<PoseMode>('single')
  const [poseBatch, setPoseBatch] = useState<File[]>([])
  const [garmentTopModeState, setGarmentTopModeState] = useState<GarmentMode>('single')
  const [garmentTopBatch, setGarmentTopBatch] = useState<File[]>([])
  const [garmentBottomModeState, setGarmentBottomModeState] = useState<GarmentMode>('single')
  const [garmentBottomBatch, setGarmentBottomBatch] = useState<File[]>([])
  const [silhouetteImage, setSilhouetteImage] = useState<File | null>(null)
  const [backgroundImage, setBackgroundImage] = useState<File | null>(null)
  const [lightingImage, setLightingImage] = useState<File | null>(null)
  const [garmentTopImage, setGarmentTopImage] = useState<File | null>(null)
  const [garmentBottomImage, setGarmentBottomImage] = useState<File | null>(null)
  const [garmentAdditionalImage, setGarmentAdditionalImage] = useState<File | null>(null)
  const [garmentExtraImage, setGarmentExtraImage] = useState<File | null>(null)
  const [bagImage, setBagImage] = useState<File | null>(null)
  const [glassesImage, setGlassesImage] = useState<File | null>(null)
  const [shoesImage, setShoesImage] = useState<File | null>(null)
  const [jewelryImage, setJewelryImage] = useState<File | null>(null)
  const [selectedModel, setSelectedModelState] = useState<ModelCodename>('strawberry-pro2')
  const [selectedModels, setSelectedModelsState] = useState<ModelCodename[]>(['strawberry-pro2'])
  const [multiModelEnabled, setMultiModelEnabledState] = useState<boolean>(false)
  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>('none')
  const [imageSize, setImageSize] = useState<ImageSizeOption>('2K')
  const [plumSize, setPlumSize] = useState<PlumV2SizeOption>('1024x1536')
  const [plumQuality, setPlumQuality] = useState<PlumV2QualityOption>('medium')
  const [transparentBackground, setTransparentBackground] = useState<boolean>(false)
  // Blueberry-specific (also safe to send for others)
  const [resolution, setResolution] = useState<ResolutionOption>('2k')
  const [outputFormat, setOutputFormat] = useState<OutputFormatOption>('PNG')
  const [fullPromptOverride, setFullPromptOverride] = useState<string>('')
  const [promptPresets, setPromptPresets] = useState<PromptPreset[]>(DEFAULT_PROMPT_PRESETS)
  const [promptPresetId, setPromptPresetId] = useState<string>('empty')
  const [prompt, setPrompt] = useState<string>('')
  const [generationResult, setGenerationResult] = useState<GenerationResult>({ status: 'idle' })

  const setSelectedModel = (model: ModelCodename) => {
    const modelChanged = model !== selectedModel
    setSelectedModelState(model)
    if (modelChanged) setTransparentBackground(false)
    if (model === 'plumv2-fast') {
      setPlumQuality('low')
    } else if (model === 'plum-v2') {
      setPlumQuality('medium')
    } else if (model === 'sunburst') {
      setPlumQuality('high')
    } else if (model === 'flare') {
      setPlumQuality('medium')
    }
    if (!multiModelEnabled) {
      setSelectedModelsState([model])
    }
  }

  const setSelectedModels = (models: ModelCodename[]) => {
    const unique = Array.from(new Set(models || [])).filter(Boolean) as ModelCodename[]
    if (unique.length === 0) {
      setSelectedModelsState([selectedModel])
      return
    }
    setSelectedModelsState(unique)
    const modelChanged = unique[0] !== selectedModel
    setSelectedModelState(unique[0])
    if (modelChanged) setTransparentBackground(false)
  }

  const setMultiModelEnabled = (enabled: boolean) => {
    setMultiModelEnabledState(enabled)
    if (enabled) {
      setSelectedModelsState(prev => (prev.length ? prev : [selectedModel]))
    } else {
      setSelectedModelsState([selectedModel])
    }
  }

  const updateFaceMode = (mode: FaceMode) => {
    setFaceModeState(mode)
    if (mode === 'single') {
      setFaceBatch([])
    } else {
      setFaceImage(null)
    }
  }

  const updatePoseMode = (mode: PoseMode) => {
    setPoseModeState(mode)
    if (mode === 'single') {
      setPoseBatch([])
    } else {
      setPoseImage(null)
    }
  }

  const updateGarmentTopMode = (mode: GarmentMode) => {
    setGarmentTopModeState(mode)
    if (mode === 'single') {
      setGarmentTopBatch([])
    } else {
      setGarmentTopImage(null)
    }
  }

  const updateGarmentBottomMode = (mode: GarmentMode) => {
    setGarmentBottomModeState(mode)
    if (mode === 'single') {
      setGarmentBottomBatch([])
    } else {
      setGarmentBottomImage(null)
    }
  }

  const addFaceFiles = (files: File[]) => {
    if (!files?.length) return
    setFaceBatch(prev => {
      const existingKeys = new Set(prev.map(file => `${file.name}-${file.size}-${file.lastModified}`))
      const next = [...prev]
      files.forEach(file => {
        const key = `${file.name}-${file.size}-${file.lastModified}`
        if (!existingKeys.has(key)) {
          existingKeys.add(key)
          next.push(file)
        }
      })
      return next
    })
  }

  const removeFaceFile = (index: number) => {
    setFaceBatch(prev => prev.filter((_, idx) => idx !== index))
  }

  const clearFaceBatch = () => setFaceBatch([])

  const addGarmentTopFiles = (files: File[]) => {
    if (!files?.length) return
    setGarmentTopBatch(prev => {
      const existingKeys = new Set(prev.map(file => `${file.name}-${file.size}-${file.lastModified}`))
      const next = [...prev]
      files.forEach(file => {
        const key = `${file.name}-${file.size}-${file.lastModified}`
        if (!existingKeys.has(key)) {
          existingKeys.add(key)
          next.push(file)
        }
      })
      return next
    })
  }

  const removeGarmentTopFile = (index: number) => {
    setGarmentTopBatch(prev => prev.filter((_, idx) => idx !== index))
  }

  const clearGarmentTopBatch = () => setGarmentTopBatch([])

  const addGarmentBottomFiles = (files: File[]) => {
    if (!files?.length) return
    setGarmentBottomBatch(prev => {
      const existingKeys = new Set(prev.map(file => `${file.name}-${file.size}-${file.lastModified}`))
      const next = [...prev]
      files.forEach(file => {
        const key = `${file.name}-${file.size}-${file.lastModified}`
        if (!existingKeys.has(key)) {
          existingKeys.add(key)
          next.push(file)
        }
      })
      return next
    })
  }

  const removeGarmentBottomFile = (index: number) => {
    setGarmentBottomBatch(prev => prev.filter((_, idx) => idx !== index))
  }

  const clearGarmentBottomBatch = () => setGarmentBottomBatch([])

  const addPoseFiles = (files: File[]) => {
    if (!files?.length) return
    setPoseBatch(prev => {
      const existingKeys = new Set(prev.map(file => `${file.name}-${file.size}-${file.lastModified}`))
      const next = [...prev]
      files.forEach(file => {
        const key = `${file.name}-${file.size}-${file.lastModified}`
        if (!existingKeys.has(key)) {
          existingKeys.add(key)
          next.push(file)
        }
      })
      return next
    })
  }

  const removePoseFile = (index: number) => {
    setPoseBatch(prev => prev.filter((_, idx) => idx !== index))
  }

  const clearPoseBatch = () => setPoseBatch([])

  const setFile = (slot: SlotName, file: File | null) => {
    switch (slot) {
      case 'faceImage':
        if (faceModeState === 'batch') {
          if (file) {
            addFaceFiles([file])
          } else {
            clearFaceBatch()
          }
        } else {
          setFaceImage(file)
        }
        break
      case 'poseImage':
        if (poseModeState === 'batch') {
          if (file) {
            addPoseFiles([file])
          } else {
            clearPoseBatch()
          }
        } else {
          setPoseImage(file)
        }
        break
      case 'silhouetteImage': setSilhouetteImage(file); break
      case 'backgroundImage': setBackgroundImage(file); break
      case 'lightingImage': setLightingImage(file); break
      case 'garmentTopImage':
        if (garmentTopModeState === 'batch') {
          if (file) {
            addGarmentTopFiles([file])
          } else {
            clearGarmentTopBatch()
          }
        } else {
          setGarmentTopImage(file)
        }
        break
      case 'garmentBottomImage':
        if (garmentBottomModeState === 'batch') {
          if (file) {
            addGarmentBottomFiles([file])
          } else {
            clearGarmentBottomBatch()
          }
        } else {
          setGarmentBottomImage(file)
        }
        break
      case 'garmentAdditionalImage': setGarmentAdditionalImage(file); break
      case 'garmentExtraImage': setGarmentExtraImage(file); break
      case 'bagImage': setBagImage(file); break
      case 'glassesImage': setGlassesImage(file); break
      case 'shoesImage': setShoesImage(file); break
      case 'jewelryImage': setJewelryImage(file); break
    }
  }
  const clearSlot = (slot: SlotName) => setFile(slot, null)

  const generateImage = async (projectName: string = 'Stylista', styleInjection?: string): Promise<boolean> => {
    try {
      const selectedList = Array.from(new Set((multiModelEnabled ? selectedModels : [selectedModel]) || []))
        .filter(Boolean) as ModelCodename[]
      const modelsToUse = selectedList.length ? selectedList : [selectedModel]
      const requireAsyncQueue = multiModelEnabled && modelsToUse.length > 1

      if (multiModelEnabled && modelsToUse.length === 0) {
        setGenerationResult({ status: 'error', message: 'Wybierz przynajmniej jeden model.' })
        return false
      }

      const faceFiles = faceModeState === 'batch'
        ? faceBatch
        : (faceImage ? [faceImage] : [])

      const poseFiles = poseModeState === 'batch'
        ? poseBatch
        : (poseImage ? [poseImage] : [])

      const garmentTopFiles = garmentTopModeState === 'batch'
        ? garmentTopBatch
        : (garmentTopImage ? [garmentTopImage] : [])

      const garmentBottomFiles = garmentBottomModeState === 'batch'
        ? garmentBottomBatch
        : (garmentBottomImage ? [garmentBottomImage] : [])

      const hasFaceReference = faceModeState === 'batch' ? faceBatch.length > 0 : Boolean(faceImage)
      const hasPoseReference = poseModeState === 'batch' ? poseBatch.length > 0 : Boolean(poseImage)
      const hasGarmentTopReference = garmentTopModeState === 'batch' ? garmentTopBatch.length > 0 : Boolean(garmentTopImage)
      const hasGarmentBottomReference = garmentBottomModeState === 'batch' ? garmentBottomBatch.length > 0 : Boolean(garmentBottomImage)

      const hasAnyReference = Boolean(
        hasFaceReference ||
        hasPoseReference ||
        hasGarmentTopReference ||
        hasGarmentBottomReference ||
        garmentAdditionalImage ||
        garmentExtraImage ||
        silhouetteImage ||
        backgroundImage ||
        lightingImage ||
        bagImage ||
        glassesImage ||
        shoesImage ||
        jewelryImage
      )

      if (!hasAnyReference) {
        setGenerationResult({ status: 'error', message: 'Wgraj przynajmniej jedno zdjęcie referencyjne' })
        return false
      }
      if (faceModeState === 'batch' && faceFiles.length === 0) {
        setGenerationResult({
          status: 'error',
          message: 'Dodaj do listy przynajmniej jedną twarz albo wróć do trybu pojedynczego.'
        })
        return false
      }
      if (poseModeState === 'batch' && poseFiles.length === 0) {
        setGenerationResult({
          status: 'error',
          message: 'Dodaj do listy przynajmniej jedną pozę albo wróć do trybu pojedynczego.'
        })
        return false
      }
      if (garmentTopModeState === 'batch' && garmentTopFiles.length === 0) {
        setGenerationResult({
          status: 'error',
          message: 'Dodaj do listy przynajmniej jeden element dla "Ubranie góra" albo wróć do trybu pojedynczego.'
        })
        return false
      }
      if (garmentBottomModeState === 'batch' && garmentBottomFiles.length === 0) {
        setGenerationResult({
          status: 'error',
          message: 'Dodaj do listy przynajmniej jeden element dla "Ubranie dół" albo wróć do trybu pojedynczego.'
        })
        return false
      }

      const fileChecks: { file: File; label: string }[] = []
      const addFile = (file: File | null, label: string) => {
        if (file) fileChecks.push({ file, label })
      }
      const addFiles = (files: File[], label: string) => {
        files.forEach(file => fileChecks.push({ file, label }))
      }

      if (faceModeState === 'batch') {
        addFiles(faceBatch, 'Twarz (lista)')
      } else {
        addFile(faceImage, 'Twarz')
      }
      if (poseModeState === 'batch') {
        addFiles(poseBatch, 'Poza (lista)')
      } else {
        addFile(poseImage, 'Poza')
      }
      if (garmentTopModeState === 'batch') {
        addFiles(garmentTopBatch, 'Ubranie góra (lista)')
      } else {
        addFile(garmentTopImage, 'Ubranie góra')
      }
      if (garmentBottomModeState === 'batch') {
        addFiles(garmentBottomBatch, 'Ubranie dół (lista)')
      } else {
        addFile(garmentBottomImage, 'Ubranie dół')
      }
      addFile(silhouetteImage, 'Sylwetka')
      addFile(backgroundImage, 'Tło')
      addFile(lightingImage, 'Oświetlenie')
      addFile(garmentAdditionalImage, 'Dodatkowe ubranie')
      addFile(garmentExtraImage, 'Dodatkowe ubranie 2')
      addFile(bagImage, 'Torebka')
      addFile(glassesImage, 'Okulary')
      addFile(shoesImage, 'Buty')
      addFile(jewelryImage, 'Biżuteria')

      for (const item of fileChecks) {
        try {
          await item.file.slice(0, 1).arrayBuffer()
        } catch {
          setGenerationResult({
            status: 'error',
            message: `Nie mogę odczytać pliku "${item.file.name}" (${item.label}). Plik mógł zostać przeniesiony lub usunięty. Załaduj go ponownie.`
          })
          return false
        }
      }

      const orNull = (files: File[]) => (files.length ? files : [null])
      const faceVariants = orNull(faceFiles)
      const poseVariants = orNull(poseFiles)
      const garmentTopVariants = orNull(garmentTopFiles)
      const garmentBottomVariants = orNull(garmentBottomFiles)
      const modelVariants = modelsToUse.length ? modelsToUse : [selectedModel]

      const jobs: {
        faceFile: File | null
        poseFile: File | null
        garmentTopFile: File | null
        garmentBottomFile: File | null
        model: ModelCodename
      }[] = []
      modelVariants.forEach(model => {
        faceVariants.forEach(faceFile => {
          poseVariants.forEach(poseFile => {
            garmentTopVariants.forEach(garmentTopFile => {
              garmentBottomVariants.forEach(garmentBottomFile => {
                jobs.push({ faceFile, poseFile, garmentTopFile, garmentBottomFile, model })
              })
            })
          })
        })
      })

      const batchEnabled = jobs.length > 1

      let batchInfo: BatchRunInfo | undefined = batchEnabled
        ? {
            enabled: true,
            poseMode: poseModeState,
            faceMode: faceModeState,
            garmentTopMode: garmentTopModeState,
            garmentBottomMode: garmentBottomModeState,
            total: jobs.length,
            completed: 0,
            activeIndex: jobs.length ? 0 : -1,
            results: []
          }
        : undefined

      setGenerationResult({
        status: 'generating',
        previewUrls: [],
        message: undefined,
        imagesGenerated: 0,
        metadata: undefined,
        batchInfo,
        modelResponse: undefined
      })

      const aggregatedPreviews: string[] = []
      const jobResults: BatchJobResult[] = []
      let successCount = 0
      let firstErrorMessage = ''
      let lastMetadata: any = null
      let lastModelResponse: string | undefined

      const collectPreviewUrls = (data: any): string[] => {
        const urls: string[] = []
        const resultUrls = Array.isArray(data?.result_urls) ? data.result_urls : []
        if (resultUrls.length > 0) {
          urls.push(...resultUrls)
        } else if (data?.result_url) {
          urls.push(data.result_url)
        } else if (Array.isArray(data?.preview_files)) {
          urls.push(...data.preview_files)
        }

        const normalized = normalizePreviewUrls(urls)
        return Array.from(new Set(normalized)).slice(0, 1)
      }

      const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

      const pollTaskStatus = async (taskId: string): Promise<{ success: boolean; previewUrls: string[]; message?: string; modelResponse?: string }> => {
        const deadline = Date.now() + (15 * 60 * 1000)
        // 5s poll cadence — reduces request load on /stylista/status by ~2.5×
        // vs the old 2s; user-perceived latency change is negligible because
        // generations take 60-600s.
        const POLL_INTERVAL_MS = 5000
        while (Date.now() < deadline) {
          const resp = await apiClient.get(`/stylista/status/${taskId}`, { timeout: 30000 })
          const data = resp.data
          if (data?.status === 'completed') {
            return { success: true, previewUrls: collectPreviewUrls(data), modelResponse: data?.model_response }
          }
          if (data?.status === 'failed') {
            return { success: false, previewUrls: [], message: data?.error || 'Generation failed', modelResponse: data?.model_response }
          }
          await sleep(POLL_INTERVAL_MS)
        }
        return { success: false, previewUrls: [], message: 'Przekroczono czas oczekiwania na wynik. Sprobuj ponownie.' }
      }

      if (requireAsyncQueue) {
        const queuedJobs: { job: typeof jobs[number]; taskId: string }[] = []

        for (let i = 0; i < jobs.length; i += 1) {
          const { faceFile, poseFile, garmentTopFile, garmentBottomFile, model } = jobs[i]

          if (batchInfo) {
            batchInfo.activeIndex = i
            setGenerationResult(prev => ({
              ...prev,
              batchInfo: { ...batchInfo }
            }))
          }

          const formData = new FormData()
          if (faceFile) formData.append('face_image', faceFile)
          if (poseFile) formData.append('pose_image', poseFile)
          if (silhouetteImage) formData.append('silhouette_image', silhouetteImage)
          if (backgroundImage) formData.append('background_image', backgroundImage)
          if (lightingImage) formData.append('lighting_image', lightingImage)
          if (garmentTopFile) formData.append('garment_top_image', garmentTopFile)
          if (garmentBottomFile) formData.append('garment_bottom_image', garmentBottomFile)
          if (garmentAdditionalImage) formData.append('garment_additional_image', garmentAdditionalImage)
          if (garmentExtraImage) formData.append('garment_extra_image', garmentExtraImage)
          if (bagImage) formData.append('bag_image', bagImage)
          if (glassesImage) formData.append('glasses_image', glassesImage)
          if (shoesImage) formData.append('shoes_image', shoesImage)
          if (jewelryImage) formData.append('jewelry_image', jewelryImage)
          formData.append('selected_model', model)
          formData.append('aspect_ratio', aspectRatio)
          formData.append('image_size', imageSize)
          formData.append('resolution', resolution)
          if (model === 'blueberry' || model === 'blueberry-45') {
            formData.append('output_format', outputFormat)
          }
          // OpenAI image options are consumed by the selected OpenAI model; only
          // models that support transparency receive the checkbox parameter.
          appendStylistaImageOptions(formData, model, plumSize, plumQuality, transparentBackground)
          // #DEBUG (2026-05-29): diagnose why worker sometimes receives
          // image_size='2K' fallback instead of the picked plumSize. Remove
          // once verified that backend logs show size=<plumSize> reliably.
          // eslint-disable-next-line no-console
          console.log('[Stylista submit]', { model, plumSize, plumQuality, imageSize, resolution })
          if (fullPromptOverride && fullPromptOverride.trim()) {
            formData.append('full_prompt', fullPromptOverride.trim())
          }
          const effectivePrompt = styleInjection
            ? [prompt, styleInjection].filter(Boolean).join('\n')
            : prompt
          formData.append('prompt', effectivePrompt)
          if (promptPresetId) formData.append('prompt_preset_id', promptPresetId)
          formData.append('project_name', projectName)

          let jobMessage = ''
          let taskId: string | null = null
          try {
            const asyncResp = await apiClient.post('/stylista/generate/async', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 30000
            })
            const asyncData = asyncResp.data
            if (asyncData?.success && asyncData?.task_id) {
              taskId = asyncData.task_id
            } else if (asyncData?.message) {
              jobMessage = asyncData.message
            }
          } catch (e: any) {
            jobMessage = e?.response?.data?.message || 'Async queue not available for multi-model generation.'
          }

          if (!taskId) {
            setGenerationResult({
              status: 'error',
              message: jobMessage || 'Async queue not available for multi-model generation.'
            })
            return false
          }
          queuedJobs.push({ job: jobs[i], taskId })
        }

        for (let i = 0; i < queuedJobs.length; i += 1) {
          const { job, taskId } = queuedJobs[i]
          const { faceFile, poseFile, garmentTopFile, garmentBottomFile, model } = job

          if (batchInfo) {
            batchInfo.activeIndex = i
            setGenerationResult(prev => ({
              ...prev,
              batchInfo: { ...batchInfo }
            }))
          }

          let jobSuccess = false
          let jobMessage = ''
          let jobPreview: string[] = []
          let jobModelResponse = ''

          const asyncResult = await pollTaskStatus(taskId)
          if (asyncResult.success) {
            jobSuccess = true
            jobPreview = asyncResult.previewUrls
            aggregatedPreviews.push(...jobPreview)
            successCount += 1
          } else {
            jobMessage = asyncResult.message || 'Generation failed'
          }
          if (asyncResult.modelResponse) {
            jobModelResponse = asyncResult.modelResponse
          }

          if (!jobSuccess && !firstErrorMessage) {
            firstErrorMessage = jobMessage
          }

          jobResults.push({
            poseName: poseFile?.name || null,
            faceName: faceFile?.name || null,
            garmentTopName: garmentTopFile?.name || null,
            garmentBottomName: garmentBottomFile?.name || null,
            success: jobSuccess,
            message: jobMessage || undefined,
            previewUrls: jobPreview,
            modelResponse: jobModelResponse || undefined,
            modelName: model
          })

          if (jobModelResponse) {
            lastModelResponse = jobModelResponse
          }

          if (batchInfo) {
            batchInfo.results = [...jobResults]
            batchInfo.completed = jobResults.length
            batchInfo.activeIndex = jobResults.length < jobs.length ? jobResults.length : -1
            setGenerationResult(prev => ({
              ...prev,
              batchInfo: { ...batchInfo }
            }))
          }
        }
      } else {
        for (let i = 0; i < jobs.length; i += 1) {
          const { faceFile, poseFile, garmentTopFile, garmentBottomFile, model } = jobs[i]

          if (batchInfo) {
            batchInfo.activeIndex = i
            setGenerationResult(prev => ({
              ...prev,
              batchInfo: { ...batchInfo }
            }))
          }

          const formData = new FormData()
          if (faceFile) formData.append('face_image', faceFile)
          if (poseFile) formData.append('pose_image', poseFile)
          if (silhouetteImage) formData.append('silhouette_image', silhouetteImage)
          if (backgroundImage) formData.append('background_image', backgroundImage)
          if (lightingImage) formData.append('lighting_image', lightingImage)
          if (garmentTopFile) formData.append('garment_top_image', garmentTopFile)
          if (garmentBottomFile) formData.append('garment_bottom_image', garmentBottomFile)
          if (garmentAdditionalImage) formData.append('garment_additional_image', garmentAdditionalImage)
          if (garmentExtraImage) formData.append('garment_extra_image', garmentExtraImage)
          if (bagImage) formData.append('bag_image', bagImage)
          if (glassesImage) formData.append('glasses_image', glassesImage)
          if (shoesImage) formData.append('shoes_image', shoesImage)
          if (jewelryImage) formData.append('jewelry_image', jewelryImage)
          formData.append('selected_model', model)
          formData.append('aspect_ratio', aspectRatio)
          formData.append('image_size', imageSize)
          formData.append('resolution', resolution)
          if (model === 'blueberry' || model === 'blueberry-45') {
            formData.append('output_format', outputFormat)
          }
          // OpenAI image options are consumed by the selected OpenAI model; only
          // models that support transparency receive the checkbox parameter.
          appendStylistaImageOptions(formData, model, plumSize, plumQuality, transparentBackground)
          // #DEBUG (2026-05-29): diagnose why worker sometimes receives
          // image_size='2K' fallback instead of the picked plumSize. Remove
          // once verified that backend logs show size=<plumSize> reliably.
          // eslint-disable-next-line no-console
          console.log('[Stylista submit]', { model, plumSize, plumQuality, imageSize, resolution })
          if (fullPromptOverride && fullPromptOverride.trim()) {
            formData.append('full_prompt', fullPromptOverride.trim())
          }
          const effectivePrompt = styleInjection
            ? [prompt, styleInjection].filter(Boolean).join('\n')
            : prompt
          formData.append('prompt', effectivePrompt)
          if (promptPresetId) formData.append('prompt_preset_id', promptPresetId)
          formData.append('project_name', projectName)

          let jobSuccess = false
          let jobMessage = ''
          let jobPreview: string[] = []
          let jobModelResponse = ''

          try {
            let usedAsync = false
            let taskId: string | null = null

            try {
              const asyncResp = await apiClient.post('/stylista/generate/async', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 30000
              })
              const asyncData = asyncResp.data
              if (asyncData?.success && asyncData?.task_id) {
                usedAsync = true
                taskId = asyncData.task_id
              } else if (asyncData?.message) {
                jobMessage = asyncData.message
              }
            } catch (e: any) {
              const statusCode = e?.response?.status
              if (statusCode !== 503) {
                throw e
              }
            }

            if (usedAsync && taskId) {
              const asyncResult = await pollTaskStatus(taskId)
              if (asyncResult.success) {
                jobSuccess = true
                jobPreview = asyncResult.previewUrls
                aggregatedPreviews.push(...jobPreview)
                successCount += 1
              } else {
                jobMessage = asyncResult.message || 'Generation failed'
              }
              if (asyncResult.modelResponse) {
                jobModelResponse = asyncResult.modelResponse
              }
            } else if (!jobMessage) {
              const resp = await apiClient.post('/stylista/generate', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 180000
              })
              const data = resp.data
              if (data?.success) {
                jobSuccess = true
                jobPreview = collectPreviewUrls(data)
                aggregatedPreviews.push(...jobPreview)
                successCount += 1
                lastMetadata = data.metadata
              } else {
                jobMessage = data?.message || 'Generation failed'
              }
              if (data?.model_response) {
                jobModelResponse = data.model_response
              }
            }
          } catch (e: any) {
            jobMessage = e?.response?.data?.message || e?.message || 'Generation failed'
            if (e?.response?.data?.model_response) {
              jobModelResponse = e.response.data.model_response
            }
          }

          if (!jobSuccess && !firstErrorMessage) {
            firstErrorMessage = jobMessage
          }

          jobResults.push({
            poseName: poseFile?.name || null,
            faceName: faceFile?.name || null,
            garmentTopName: garmentTopFile?.name || null,
            garmentBottomName: garmentBottomFile?.name || null,
            success: jobSuccess,
            message: jobMessage || undefined,
            previewUrls: jobPreview,
            modelResponse: jobModelResponse || undefined,
            modelName: model
          })

          if (jobModelResponse) {
            lastModelResponse = jobModelResponse
          }

          if (batchInfo) {
            batchInfo.results = [...jobResults]
            batchInfo.completed = jobResults.length
            batchInfo.activeIndex = jobResults.length < jobs.length ? jobResults.length : -1
            setGenerationResult(prev => ({
              ...prev,
              batchInfo: { ...batchInfo }
            }))
          }
        }
      }

      const finalBatchInfo = batchInfo
        ? { ...batchInfo, results: [...jobResults], completed: jobResults.length, activeIndex: -1 }
        : undefined

      if (successCount > 0) {
        const allSucceeded = successCount === jobs.length
        setGenerationResult({
          status: 'success',
          previewUrls: aggregatedPreviews,
          imagesGenerated: aggregatedPreviews.length,
          message: allSucceeded
            ? `Wygenerowano ${successCount}/${jobs.length} zadań.`
            : `Uwaga: ${successCount}/${jobs.length} zadań zakończyło się sukcesem. ${firstErrorMessage || ''}`.trim(),
          metadata: lastMetadata,
          batchInfo: finalBatchInfo,
          modelResponse: lastModelResponse
        })
        return allSucceeded
      }

      setGenerationResult({
        status: 'error',
        message: firstErrorMessage || 'Generation failed',
        batchInfo: finalBatchInfo,
        modelResponse: lastModelResponse
      })
      return false
    } catch (e: any) {
      setGenerationResult({
        status: 'error',
        message: e?.response?.data?.message || e?.message || 'Generation failed'
      })
      return false
    }
  }

  const resetState = () => {
    setFaceImage(null)
    setFaceModeState('single')
    setFaceBatch([])
    setPoseImage(null)
    setPoseModeState('single')
    setPoseBatch([])
    setGarmentTopModeState('single')
    setGarmentTopBatch([])
    setGarmentBottomModeState('single')
    setGarmentBottomBatch([])
    setSilhouetteImage(null)
    setBackgroundImage(null)
    setLightingImage(null)
    setGarmentTopImage(null)
    setGarmentBottomImage(null)
    setGarmentAdditionalImage(null)
    setGarmentExtraImage(null)
    setBagImage(null)
    setGlassesImage(null)
    setShoesImage(null)
    setJewelryImage(null)
    setSelectedModelState('blueberry')
    setSelectedModelsState(['blueberry'])
    setMultiModelEnabledState(false)
    setAspectRatio('2:3')
    setImageSize('2K')
    setResolution('2k')
    setPlumSize('1024x1536')
    setPlumQuality('medium')
    setTransparentBackground(false)
    setOutputFormat('PNG')
    setFullPromptOverride('')
    setPromptPresetId('empty')
    setPrompt('')
    setGenerationResult({ status: 'idle' })
  }

  const value: StylistaContextType = {
    faceImage,
    faceMode: faceModeState,
    faceBatch,
    poseImage,
    garmentTopMode: garmentTopModeState,
    garmentTopBatch,
    garmentBottomMode: garmentBottomModeState,
    garmentBottomBatch,
    silhouetteImage,
    backgroundImage,
    lightingImage,
    garmentTopImage,
    garmentBottomImage,
    garmentAdditionalImage,
    garmentExtraImage,
    bagImage,
    glassesImage,
    shoesImage,
    jewelryImage,
    selectedModel,
    selectedModels,
    multiModelEnabled,
    aspectRatio,
    imageSize,
    resolution,
    plumSize,
    plumQuality,
    transparentBackground,
    outputFormat,
    fullPromptOverride,
    prompt,
    promptPresetId,
    promptPresets,
    poseMode: poseModeState,
    poseBatch,
    generationResult,
    setFile,
    clearSlot,
    setSelectedModel,
    setSelectedModels,
    setMultiModelEnabled,
    setAspectRatio,
    setImageSize,
    setResolution,
    setPlumSize,
    setPlumQuality,
    setTransparentBackground,
    setOutputFormat,
    setFullPromptOverride,
    setPrompt,
    setPromptPresetId,
    setPromptPresets,
    setFaceMode: updateFaceMode,
    addFaceFiles,
    removeFaceFile,
    clearFaceBatch,
    setGarmentTopMode: updateGarmentTopMode,
    addGarmentTopFiles,
    removeGarmentTopFile,
    clearGarmentTopBatch,
    setGarmentBottomMode: updateGarmentBottomMode,
    addGarmentBottomFiles,
    removeGarmentBottomFile,
    clearGarmentBottomBatch,
    setPoseMode: updatePoseMode,
    addPoseFiles,
    removePoseFile,
    clearPoseBatch,
    generateImage,
    resetState
  }

  return (
    <StylistaContext.Provider value={value}>{children}</StylistaContext.Provider>
  )
}
